import React, { useState, useEffect } from 'react';
import { Card, Button, Modal, Form, Input, Select, DatePicker, List, Collapse, message, Space, Typography } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, CommentOutlined } from '@ant-design/icons';
import { useNavigate, useParams, useLocation } from 'react-router';
import { treatmentApi } from '../api/treatment';
import { TreatmentRecord, TreatmentVisibility } from '../api/model';
import { CreateTreatmentRecordRequest, UpdateTreatmentRecordRequest, AddTreatmentRecordCommentRequest } from '../api/treatment';
import { useCurrentUser } from '../util/hooks';
import dayjs from 'dayjs';

const { Option } = Select;
const { Title, Text } = Typography;

interface LocationState {
  patientName?: string;
}

const TreatmentRecords: React.FC = () => {
  const { patientId } = useParams<{ patientId: string }>();
  const location = useLocation();
  const state = location.state as LocationState;
  const [patientName, setPatientName] = useState(state?.patientName || '');
  const navigate = useNavigate();
  const [treatmentRecords, setTreatmentRecords] = useState<TreatmentRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [updateModalVisible, setUpdateModalVisible] = useState(false);
  const [currentRecord, setCurrentRecord] = useState<TreatmentRecord | null>(null);
  const [form] = Form.useForm();
  const [updateForm] = Form.useForm();
  const [commentForms, setCommentForms] = useState<Record<string, string>>({});
  const currentUser = useCurrentUser();
  const [modal, contextHolder] = Modal.useModal();
  
  // 检查当前医生是否为记录创建者
  const isRecordOwner = (record: TreatmentRecord) => {
    // 添加安全检查，确保所有必要的对象都存在
    if (!record || !record.doctor || !record.doctor.user || !currentUser || !currentUser.id) {
      return false;
    }
    return record.doctor.user.id === currentUser.id;
  };
  
  // 治疗类型数据（模拟数据，实际应该从API获取）
  const [treatmentTypes] = useState([
    { id: 1, name: '针灸治疗' },
    { id: 2, name: '推拿按摩' },
    { id: 3, name: '中药调理' },
    { id: 4, name: '拔罐疗法' },
    { id: 5, name: '艾灸治疗' },
  ]);
  
  // 可见性选项
  const visibilityOptions = [
    { value: TreatmentVisibility.Mutual, label: '医生和患者可见' },
    { value: TreatmentVisibility.DoctorOnly, label: '仅医生可见' },
  ];

  function visibilityName(visibility: string) {
    switch (visibility) {
      case TreatmentVisibility.Mutual:
        return '医生和患者可见';
      case TreatmentVisibility.DoctorOnly:
        return '仅医生可见';
      default:
        return '未知';
    }
  }

  // 获取治疗记录列表
  const fetchTreatmentRecords = async () => {
    setLoading(true);
    try {
      const response = await treatmentApi.getTreatmentRecordsByPatientID(patientId!);
      if (response.success) {
        setTreatmentRecords(response.data.data);
      } else {
        message.error(response.message || '获取治疗记录失败');
      }
    } catch (error) {
      console.error('获取治疗记录失败:', error);
      message.error('网络错误，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  // 提交创建表单
  const handleCreateSubmit = async (values: any) => {
    if (!patientId) return;
    
    try {
      const requestData: CreateTreatmentRecordRequest = {
        patient_id: patientId,
        type_id: values.type_id,
        treatment_date: values.treatment_date.format('YYYY-MM-DD'),
        visibility: values.visibility,
        content: values.content,
      };

      const response = await treatmentApi.createTreatmentRecord(requestData);
      if (response.success) {
        message.success('治疗记录创建成功');
        setCreateModalVisible(false);
        form.resetFields();
        // 重新获取治疗记录列表
        fetchTreatmentRecords();
      } else {
        message.error(response.message || '创建治疗记录失败');
      }
    } catch (error) {
      console.error('创建治疗记录失败:', error);
      message.error('网络错误，请稍后重试');
    }
  };

  // 提交更新表单
  const handleUpdateSubmit = async (values: any) => {
    if (!currentRecord || !patientId) return;
    
    try {
      const requestData: UpdateTreatmentRecordRequest = {
        type_id: values.type_id,
        treatment_date: values.treatment_date.format('YYYY-MM-DD'),
        visibility: values.visibility,
        content: values.content,
      };

      const response = await treatmentApi.updateTreatmentRecord(currentRecord.id, requestData);
      if (response.success) {
        message.success('治疗记录更新成功');
        setUpdateModalVisible(false);
        updateForm.resetFields();
        setCurrentRecord(null);
        // 重新获取治疗记录列表
        fetchTreatmentRecords();
      } else {
        message.error(response.message || '更新治疗记录失败');
      }
    } catch (error) {
      console.error('更新治疗记录失败:', error);
      message.error('网络错误，请稍后重试');
    }
  };

  // 删除治疗记录
  const handleDelete = async (recordId: string) => {
    modal.confirm({
      title: '确认删除',
      content: '确定要删除这条治疗记录吗？此操作不可恢复。',
      okText: '确认',
      cancelText: '取消',
      onOk: async () => {
        try {
          const response = await treatmentApi.deleteTreatmentRecord(recordId);
          if (response.success) {
            message.success('治疗记录删除成功');
            // 重新获取治疗记录列表
            fetchTreatmentRecords();
          } else {
            message.error(response.message || '删除治疗记录失败');
          }
        } catch (error) {
          console.error('删除治疗记录失败:', error);
          message.error('网络错误，请稍后重试');
        }
      },
    });
  };

  // 添加评论
  const handleAddComment = async (recordId: string) => {
    const content = commentForms[recordId];
    if (!content) {
      message.warning('请输入评论内容');
      return;
    }
    
    try {
      const requestData: AddTreatmentRecordCommentRequest = {
        content,
      };

      const response = await treatmentApi.addTreatmentRecordComment(recordId, requestData);
      if (response.success) {
        message.success('评论添加成功');
        // 清空评论输入框
        setCommentForms(prev => ({ ...prev, [recordId]: '' }));
        // 重新获取治疗记录列表
        fetchTreatmentRecords();
      } else {
        message.error(response.message || '添加评论失败');
      }
    } catch (error) {
      console.error('添加评论失败:', error);
      message.error('网络错误，请稍后重试');
    }
  };

  // 初始化获取数据
  useEffect(() => {
    if (!patientName && patientId) {
      // 这里可以调用API获取患者详细信息
      // 暂时使用患者ID作为名称
      setPatientName(`患者${patientId}`);
    }
    
    if (patientId) {
      fetchTreatmentRecords();
    }
  }, [patientId, patientName]);

  // 打开更新模态框
  const openUpdateModal = (record: TreatmentRecord) => {
    setCurrentRecord(record);
    setUpdateModalVisible(true);
    // 设置表单初始值
    updateForm.setFieldsValue({
      type_id: record.type_id,
      treatment_date: dayjs(record.treatment_date),
      visibility: record.visibility,
      content: record.content,
    });
  };

  // 处理评论输入变化
  const handleCommentChange = (recordId: string, value: string) => {
    setCommentForms(prev => ({ ...prev, [recordId]: value }));
  };

  // 获取患者姓名
  useEffect(() => {
    if (!patientName && patientId) {
      // 这里可以调用API获取患者详细信息
      // 暂时使用患者ID作为名称
      setPatientName(`患者${patientId}`);
    }
  }, [patientName, patientId]);

  return (
    <div>
      {contextHolder}
      <Card 
        title={`治疗记录 - ${patientName}`} 
        extra={
            <Space>
              <Button onClick={() => navigate('/patients')}>
                返回患者列表
              </Button>
              <Button 
                type="primary" 
                icon={<PlusOutlined />} 
                onClick={() => setCreateModalVisible(true)}
              >
                添加治疗记录
              </Button>
            </Space>
          }
      >
        <List
          loading={loading}
          dataSource={treatmentRecords}
          renderItem={record => (
            <Card 
              key={record.id} 
              style={{ marginBottom: 16 }}
            >
              <div style={{ position: 'relative', paddingRight: isRecordOwner(record) ? 80 : 0 }}>
                <div>
                  <Title level={4}>{record.type_id || '未知类型'}</Title>
                  <Text>治疗日期: {record.treatment_date}</Text>
                  <br />
                  <Text>医生: {record.doctor?.user?.name || '未知医生'}</Text>
                  <br />
                  <Text>可见性: {visibilityName(record.visibility)}</Text>
                  <br />
                  <Text>创建时间: {new Date(record.create_time).toLocaleString()}</Text>
                  <br />
                  <Text>内容: {record.content}</Text>
                </div>
                {isRecordOwner(record) && (
                  <div style={{ position: 'absolute', top: 0, right: 0, display: 'flex', alignItems: 'center' }}>
                    <Button 
                      type="link" 
                      icon={<EditOutlined />} 
                      onClick={() => openUpdateModal(record)}
                      style={{ padding: '4px 8px' }}
                    >
                      编辑
                    </Button>
                    <Button 
                      type="link" 
                      icon={<DeleteOutlined />} 
                      danger
                      onClick={() => handleDelete(record.id)}
                      style={{ padding: '4px 8px' }}
                    >
                      删除
                    </Button>
                  </div>
                )}
              </div>
              
              <div style={{ marginTop: 16 }}>
                <Title level={5}>评论</Title>
                {record.comments && record.comments.length > 0 ? (
                  <List
                    dataSource={record.comments}
                    renderItem={comment => (
                      <Card size="small" style={{ marginBottom: 8 }}>
                        <Text strong>{comment.creator_name || '未知用户'}:</Text> {comment.content}
                        <br />
                        <Text type="secondary">{new Date(comment.create_time).toLocaleString()}</Text>
                      </Card>
                    )}
                  />
                ) : (
                  <Text type="secondary">暂无评论</Text>
                )}
                
                <div style={{ marginTop: 16 }}>
                  <Input.TextArea 
                    placeholder="添加评论..." 
                    value={commentForms[record.id] || ''}
                    onChange={e => handleCommentChange(record.id, e.target.value)}
                    autoSize={{ minRows: 2, maxRows: 6 }}
                  />
                  <Button 
                    type="primary" 
                    icon={<CommentOutlined />} 
                    onClick={() => handleAddComment(record.id)}
                    style={{ marginTop: 8 }}
                    disabled={!isRecordOwner(record)}
                  >
                    提交评论
                  </Button>
                </div>
              </div>
            </Card>
          )}
        />
      </Card>

      {/* 创建治疗记录模态框 */}
      <Modal
        title="新建治疗记录"
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        okText="创建"
        cancelText="取消"
      >
        <Form form={form} layout="vertical" onFinish={handleCreateSubmit}>
          <Form.Item
            name="type_id"
            label="治疗类型"
            rules={[{ required: true, message: '请选择治疗类型' }]}
          >
            <Select placeholder="请选择治疗类型">
              {treatmentTypes.map(type => (
                <Option key={type.id} value={type.id}>
                  {type.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="treatment_date"
            label="治疗日期"
            rules={[{ required: true, message: '请选择治疗日期' }]}
          >
            <DatePicker placeholder="请选择治疗日期" style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="visibility"
            label="可见性"
            rules={[{ required: true, message: '请选择可见性' }]}
          >
            <Select placeholder="请选择可见性">
              {visibilityOptions.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="content"
            label="治疗记录内容"
            rules={[{ required: true, message: '请输入治疗记录内容' }]}
          >
            <Input.TextArea placeholder="请输入治疗记录内容" autoSize={{ minRows: 3, maxRows: 10 }} />
          </Form.Item>
        </Form>
      </Modal>

      {/* 更新治疗记录模态框 */}
      <Modal
        title="编辑治疗记录"
        open={updateModalVisible}
        onCancel={() => {
          setUpdateModalVisible(false);
          updateForm.resetFields();
          setCurrentRecord(null);
        }}
        onOk={() => updateForm.submit()}
        okText="更新"
        cancelText="取消"
      >
        <Form form={updateForm} layout="vertical" onFinish={handleUpdateSubmit}>
          <Form.Item
            name="type_id"
            label="治疗类型"
            rules={[{ required: true, message: '请选择治疗类型' }]}
          >
            <Select placeholder="请选择治疗类型">
              {treatmentTypes.map(type => (
                <Option key={type.id} value={type.id}>
                  {type.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="treatment_date"
            label="治疗日期"
            rules={[{ required: true, message: '请选择治疗日期' }]}
          >
            <DatePicker placeholder="请选择治疗日期" style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="visibility"
            label="可见性"
            rules={[{ required: true, message: '请选择可见性' }]}
          >
            <Select placeholder="请选择可见性">
              {visibilityOptions.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
          
          <Form.Item
            name="content"
            label="治疗记录内容"
            rules={[{ required: true, message: '请输入治疗记录内容' }]}
          >
            <Input.TextArea placeholder="请输入治疗记录内容" autoSize={{ minRows: 3, maxRows: 10 }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default TreatmentRecords;